"use client";

import { useState, useId } from "react";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { EyebrowBadge, Input, Textarea, FormError, brandActionClasses } from "@/components/ui";
import { fieldTouchedError } from "@/lib/form-helpers";
import { CheckCircle2, Send } from "@/components/icons";
import { useFormik } from "formik";
import { discordInviteSchema, WHY_JOIN } from "./data";
import { BenefitCard } from "./parts";
import { cn } from "@/lib/utils";

export default function DiscordSection() {
  const prefersReducedMotion = useReducedMotion();
  const formId = useId();

  const [formState, setFormState] = useState<"idle" | "submitting" | "success">("idle");

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      discordUsername: "",
      reason: "",
    },
    validationSchema: discordInviteSchema,
    onSubmit: async () => {
      setFormState("submitting");
      await new Promise((res) => setTimeout(res, 1400));
      setFormState("success");
    },
  });

  return (
    <main id="main-content" tabIndex={-1} className="overflow-hidden pb-32">
      {/* ── Hero ──────────────────────────────────────────────────────────────── */}
      <section
        className="border-border relative overflow-hidden border-b pt-32 pb-20"
        aria-labelledby="discord-heading"
      >
        <div
          className="pointer-events-none absolute top-0 left-1/2 h-[400px] w-[700px] -translate-x-1/2 rounded-full opacity-[0.07] blur-3xl"
          style={{
            background:
              "radial-gradient(ellipse, color-mix(in srgb, var(--foreground) 6%, transparent) 0%, transparent 70%)",
          }}
          aria-hidden="true"
        />

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <EyebrowBadge>Proxidize Discord</EyebrowBadge>
            </motion.div>

            <motion.h1
              id="discord-heading"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.08 }}
              className="text-foreground mb-5 text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl"
            >
              The Proxidize Discord Server is currently invite only!
            </motion.h1>

            <motion.p
              initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="text-muted-foreground mx-auto max-w-xl text-lg leading-relaxed"
            >
              If you&rsquo;d like to have the chance to join, sign up to be the first to access
              exclusive updates, expert discussions, and insider tips tailored for professionals in
              the proxy industry.
            </motion.p>
          </div>
        </div>
      </section>

      {/* ── Why Join ─────────────────────────────────────────────────────────── */}
      <section className="mx-auto mt-20 mb-24 max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="mb-10"
        >
          <h2 className="text-foreground mb-3 text-3xl font-bold sm:text-4xl">
            Why Join Our Discord?
          </h2>
          <p className="text-muted-foreground max-w-lg leading-relaxed">
            An exclusive community curated for professionals who take the proxy industry seriously.
          </p>
        </motion.div>

        <div className="grid gap-5 sm:grid-cols-3">
          {WHY_JOIN.map((item, i) => (
            <BenefitCard key={item.title} index={i} {...item} />
          ))}
        </div>
      </section>

      {/* ── Request an Invite form ────────────────────────────────────────────── */}
      <section id="request-invite" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl">
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={scrollRevealViewport}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="mb-8"
          >
            <h2 className="text-foreground mb-3 text-3xl font-bold sm:text-4xl">
              Request an invite
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Fill in your details below and we&rsquo;ll review your request. We&rsquo;ll send your
              invite as soon as spots open up.
            </p>
          </motion.div>

          {formState === "success" ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="border-border bg-card rounded-2xl border p-10 text-center"
            >
              <div className="bg-foreground/10 text-brand mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full">
                <CheckCircle2 size={28} />
              </div>
              <h3 className="text-foreground mb-2 text-lg font-semibold">
                You&rsquo;re on the list!
              </h3>
              <p className="text-muted-foreground mx-auto max-w-sm text-sm leading-relaxed">
                Thanks for signing up. We&rsquo;ll review your request and send your invite as soon
                as spots open up.
              </p>
            </motion.div>
          ) : (
            <motion.form
              initial={prefersReducedMotion ? false : { opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={scrollRevealViewport}
              transition={{ duration: 0.45, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              onSubmit={formik.handleSubmit}
              className="border-border bg-card space-y-5 rounded-2xl border p-6 sm:p-8"
              aria-label="Discord invite request form"
              noValidate
            >
              {/* Name + Email */}
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor={`${formId}-name`}
                    className="text-foreground mb-1.5 block text-xs font-semibold tracking-wide uppercase"
                  >
                    Name <span className="text-red-400">*</span>
                  </label>
                  <Input
                    id={`${formId}-name`}
                    name="name"
                    type="text"
                    placeholder="Your full name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="bg-background h-10"
                  />
                  {fieldTouchedError(formik, "name") && (
                    <FormError className="mt-1">{fieldTouchedError(formik, "name")}</FormError>
                  )}
                </div>
                <div>
                  <label
                    htmlFor={`${formId}-email`}
                    className="text-foreground mb-1.5 block text-xs font-semibold tracking-wide uppercase"
                  >
                    Email <span className="text-red-400">*</span>
                  </label>
                  <Input
                    id={`${formId}-email`}
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="bg-background h-10"
                  />
                  {fieldTouchedError(formik, "email") && (
                    <FormError className="mt-1">{fieldTouchedError(formik, "email")}</FormError>
                  )}
                </div>
              </div>

              {/* Discord Username */}
              <div>
                <label
                  htmlFor={`${formId}-discord`}
                  className="text-foreground mb-1.5 block text-xs font-semibold tracking-wide uppercase"
                >
                  Discord Username <span className="text-red-400">*</span>
                </label>
                <Input
                  id={`${formId}-discord`}
                  name="discordUsername"
                  type="text"
                  placeholder="e.g. username or username#0000"
                  value={formik.values.discordUsername}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="bg-background h-10"
                />
                {fieldTouchedError(formik, "discordUsername") && (
                  <FormError className="mt-1">
                    {fieldTouchedError(formik, "discordUsername")}
                  </FormError>
                )}
              </div>

              {/* Reason */}
              <div>
                <label
                  htmlFor={`${formId}-reason`}
                  className="text-foreground mb-1.5 block text-xs font-semibold tracking-wide uppercase"
                >
                  Why are you interested in joining? <span className="text-red-400">*</span>
                </label>
                <Textarea
                  id={`${formId}-reason`}
                  name="reason"
                  rows={4}
                  placeholder="Tell us a bit about yourself and why you'd like to join the community."
                  value={formik.values.reason}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="bg-background resize-none"
                />
                {fieldTouchedError(formik, "reason") && (
                  <FormError className="mt-1">{fieldTouchedError(formik, "reason")}</FormError>
                )}
              </div>

              {/* Submit */}
              <div className="flex items-center justify-between gap-4 pt-1">
                <p className="text-muted-foreground text-xs">
                  We review every request and will reach out when spots open.
                </p>
                <button
                  type="submit"
                  disabled={formState === "submitting" || formik.isSubmitting}
                  className={cn(
                    brandActionClasses,
                    "inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-xl px-6 py-2.5 text-sm font-semibold transition-all duration-200 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                  )}
                >
                  {formState === "submitting" ? (
                    <>
                      <span
                        className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
                        aria-hidden="true"
                      />
                      Submitting…
                    </>
                  ) : (
                    <>
                      Send request
                      <Send size={14} />
                    </>
                  )}
                </button>
              </div>
            </motion.form>
          )}
        </div>
      </section>
    </main>
  );
}
